import { create } from 'zustand';
import { DrawingState, Element, ToolType, HistoryEntry } from '../types/drawing';
import { updateElement } from '../utils/canvasUtils';
import { getElementById } from '../domain/accessors';
import { devLog } from '../runtime/logger';
import { applyHistoryAction } from './historyReducer';

interface DrawingStore extends DrawingState {
  // Actions
  setCurrentTool: (tool: ToolType) => void;
  setStrokeWidth: (width: number) => void;
  setStrokeColor: (color: string) => void;
  setFillColor: (color: string) => void;
  setFillOpacity: (opacity: number) => void;
  setIsDrawing: (drawing: boolean) => void;
  addElement: (element: Element) => void;
  updateElement: (id: string, updates: Partial<Element>) => void;
  deleteElement: (id: string) => void;
  selectElement: (id: string) => void;
  deselectElement: (id: string) => void;
  clearSelection: () => void;
  addToHistory: (entry: HistoryEntry) => void;
  undo: () => void;
  redo: () => void;
  clearCanvas: () => void;
}

const initialState: DrawingState = {
  currentTool: 'pencil',
  strokeWidth: 3,
  strokeColor: '#000000',
  fillColor: '#ffffff',
  fillOpacity: 1,
  isDrawing: false,
  selectedElements: [],
  elements: [],
  history: [],
  currentLayer: 'main',
};

export const useDrawingStore = create<DrawingStore>((set, get) => ({
  ...initialState,

  setCurrentTool: (tool) => {
    devLog('Setting current tool:', tool);
    set({ currentTool: tool });
  },

  setStrokeWidth: (width) => set({ strokeWidth: width }),

  setStrokeColor: (color) => set({ strokeColor: color }),

  setFillColor: (color) => set({ fillColor: color }),

  setFillOpacity: (opacity) => set({ fillOpacity: opacity }),

  setIsDrawing: (drawing) => set({ isDrawing: drawing }),

  addElement: (element) => {
    devLog('Adding element:', element);
    set((state) => ({
      elements: [...state.elements, element],
      history: [
        ...state.history,
        {
          action: 'add',
          elementId: element.id,
          element,
          timestamp: new Date(),
        },
      ],
    }));
  },

  updateElement: (id, updates) => {
    set((state) => {
      const target = getElementById(state.elements, id);
      return {
        elements: state.elements.map((el) =>
          el.id === id ? updateElement(el, updates) : el
        ),
        history: [
          ...state.history,
          ...(target
            ? [
                {
                  action: 'modify' as const,
                  elementId: id,
                  element: target,
                  timestamp: new Date(),
                },
              ]
            : []),
        ],
      };
    });
  },

  deleteElement: (id) => {
    set((state) => {
      const target = getElementById(state.elements, id);
      return {
        elements: state.elements.filter((el) => el.id !== id),
        selectedElements: state.selectedElements.filter((el) => el.id !== id),
        history: [
          ...state.history,
          ...(target
            ? [
                {
                  action: 'delete' as const,
                  elementId: id,
                  element: target,
                  timestamp: new Date(),
                },
              ]
            : []),
        ],
      };
    });
  },

  selectElement: (id) => {
    set((state) => {
      const found = getElementById(state.elements, id);
      return found
        ? { selectedElements: [...state.selectedElements, found] }
        : { selectedElements: state.selectedElements };
    });
  },

  deselectElement: (id) => {
    set((state) => ({
      selectedElements: state.selectedElements.filter((el) => el.id !== id),
    }));
  },

  clearSelection: () => set({ selectedElements: [] }),

  addToHistory: (entry) => {
    set((state) => ({
      history: [...state.history, entry],
    }));
  },

  undo: () => {
    const current = get();
    const next = applyHistoryAction(
      {
        elements: current.elements,
        history: current.history,
        selectedElements: current.selectedElements,
      },
      { type: 'undo' }
    );
    if (
      next.elements === current.elements &&
      next.history === current.history &&
      next.selectedElements === current.selectedElements
    ) {
      return;
    }
    set(() => ({
      elements: next.elements,
      history: next.history,
      selectedElements: next.selectedElements,
    }));
  },

  redo: () => {
    const current = get();
    const next = applyHistoryAction(
      {
        elements: current.elements,
        history: current.history,
        selectedElements: current.selectedElements,
      },
      { type: 'redo' }
    );
    if (
      next.elements === current.elements &&
      next.history === current.history &&
      next.selectedElements === current.selectedElements
    ) {
      return;
    }
    set(() => ({
      elements: next.elements,
      history: next.history,
      selectedElements: next.selectedElements,
    }));
  },

  clearCanvas: () => {
    set((state) => {
      const snapshot = state.elements;
      return {
        elements: [],
        selectedElements: [],
        history: [
          ...state.history,
          {
            action: 'clear',
            elementId: 'clear-all',
            elementsSnapshot: snapshot,
            timestamp: new Date(),
          },
        ],
      };
    });
  },
}));

// Debug: Log store initialization
devLog('Drawing store initialized with state:', initialState);
